<template>
  <div class="modal fade" id="chooseStudentByUcoModalForm" tabindex="-1" aria-labelledby="chooseStudentByUcoModalFormLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="chooseStudentByUcoModalFormLabel">{{ title }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3 position-relative">
            <input
              v-model="searchQuery"
              type="text"
              :class="['form-control pe-4', { 'border-danger': ucoNotFound }]"
              placeholder="Search by UCO or name..."
              @input="onSearchInput"
            />
            <span v-if="searchQuery" class="position-absolute top-50 end-0 translate-middle-y me-2" style="cursor:pointer;" @click="clearSearch">
              <i class="bi bi-x"></i>
            </span>
          </div>

          <div v-if="ucoNotFound" class="text-danger small mb-2">UCO does not exist.</div>
          <div v-if="studentsLoadError" class="text-warning small mb-2">Could not load student list — you can still type a UCO directly.</div>

          <div v-if="filteredStudents.length > 0" class="list-group mb-3" style="max-height:250px; overflow-y:auto;">
            <button
              v-for="s in filteredStudents"
              :key="s.uco"
              type="button"
              :class="['list-group-item list-group-item-action d-flex justify-content-between align-items-center', { active: selectedUcos.includes(String(s.uco)) }]"
              @click="toggleStudent(s)"
            >
              <span>
                <strong>{{ s.uco }}</strong>
                <span v-if="s.name" class="ms-2" :class="selectedUcos.includes(String(s.uco)) ? 'text-white-50' : 'text-muted'">{{ s.name }}</span>
              </span>
              <i v-if="selectedUcos.includes(String(s.uco))" class="bi bi-check2"></i>
            </button>
          </div>

          <div v-if="selectedUcos.length > 0 && allowMultiple" class="mb-3">
            <div class="d-flex flex-wrap gap-1">
              <span v-for="uco in selectedUcos" :key="uco" class="badge bg-primary d-flex align-items-center gap-1">
                {{ uco }}
                <span style="cursor:pointer;" @click="removeUco(uco)"><i class="bi bi-x"></i></span>
              </span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="resetForm">Close</button>
          <button type="button" class="btn btn-primary" :disabled="!canSubmit" @click="submitForm">Submit</button>
        </div>
      </div>
    </div>
  </div>

  <StemErrorModal ref="stemErrorModal" />
</template>

<script>
import { Modal } from 'bootstrap';
import StemErrorModal from '@/components/StemErrorModal.vue';
import StudentController from '@/controllers/StudentController';

export default {
  name: 'ChooseStudentByUcoModalForm',
  components: { StemErrorModal },
  props: {
    title: {
      type: String,
      default: 'Search Student'
    },
    allowMultiple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      allStudents: [],
      filteredStudents: [],
      selectedUcos: [],
      ucoNotFound: false,
      studentsLoadError: false,
      studentsLoaded: false,
    };
  },
  computed: {
    canSubmit() {
      if (this.selectedUcos.length > 0) return true;
      const raw = this.searchQuery.trim();
      return raw !== '' && !isNaN(raw);
    }
  },
  mounted() {
    const modalElement = document.getElementById('chooseStudentByUcoModalForm');
    modalElement.addEventListener('show.bs.modal', this.onModalOpen);
    modalElement.addEventListener('hidden.bs.modal', this.resetForm);
  },
  methods: {
    async onModalOpen() {
      if (this.studentsLoaded) { this.filterStudents(); return; }
      try {
        this.allStudents = await StudentController.fetchAllStudents();
        this.studentsLoaded = true;
        this.filterStudents();
      } catch {
        this.studentsLoadError = true;
      }
    },
    onSearchInput() {
      this.ucoNotFound = false;
      if (!this.allowMultiple) this.selectedUcos = [];
      this.filterStudents();
    },
    filterStudents() {
      const q = this.searchQuery.toLowerCase();
      this.filteredStudents = this.allStudents.filter(s =>
        String(s.uco).includes(q) ||
        (s.name && s.name.toLowerCase().includes(q))
      );
    },
    toggleStudent(s) {
      const uco = String(s.uco);
      if (this.allowMultiple) {
        const idx = this.selectedUcos.indexOf(uco);
        if (idx === -1) {
          this.selectedUcos.push(uco);
        } else {
          this.selectedUcos.splice(idx, 1);
        }
      } else {
        this.selectedUcos = [uco];
        this.searchQuery = `${s.uco}${s.name ? ' — ' + s.name : ''}`;
        this.filteredStudents = [];
      }
    },
    removeUco(uco) {
      this.selectedUcos = this.selectedUcos.filter(u => u !== uco);
    },
    clearSearch() {
      this.searchQuery = '';
      if (!this.allowMultiple) {
        this.selectedUcos = [];
      }
      this.filterStudents();
    },
    submitForm() {
      let result;
      if (this.selectedUcos.length > 0) {
        result = this.allowMultiple ? this.selectedUcos.join(', ') : this.selectedUcos[0];
      } else {
        const raw = this.searchQuery.trim();
        if (!raw || isNaN(raw)) {
          this.$refs.stemErrorModal.show('Error', 'Please select a student or enter a valid UCO.');
          return;
        }
        if (this.studentsLoaded && !this.allStudents.some(s => String(s.uco) === raw)) {
          this.ucoNotFound = true;
          return;
        }
        result = raw;
      }

      const modalElement = document.getElementById('chooseStudentByUcoModalForm');
      const modalInstance = Modal.getInstance(modalElement);
      if (modalInstance) modalInstance.hide();

      this.$emit('submitForm', result);
    },
    resetForm() {
      this.searchQuery = '';
      this.filteredStudents = [];
      this.selectedUcos = [];
      this.ucoNotFound = false;
    },
  }
};
</script>
